/**
 * Randomising an option list for a student, and putting it back in that order afterwards.
 *
 * Dropdown and MCQ are the two blocks whose options can be shuffled, and both had their own copy
 * of these. Byte-identical apart from the option type — except that MCQ's reorder had lost its doc
 * comment and been renamed to `reorderMcqOptionsByIds`, which is how a shared idea stops looking
 * like one.
 *
 * Generic over the option shape rather than typed to either block's: all these need is an id and
 * an anchored flag, and nothing here should have an opinion about roles, media or formulas.
 */

/** The minimum an option must have to take part in ordering. */
export interface OrderableOption {
  id: string;
  /** Held at its authored index while everything around it moves. */
  anchored?: boolean;
}

/**
 * Shuffles options for student display, keeping any anchored option fixed at its authored index.
 *
 * Fisher-Yates over the un-anchored ones only, then dealt back into the gaps the anchored ones
 * leave. Shuffling everything and then swapping anchored items into place would be the obvious
 * alternative and is wrong: it displaces whichever option happened to land there, so an anchored
 * option can push a different one out of a position it was also anchored to.
 */
export function shuffleWithAnchors<T extends OrderableOption>(options: T[]): T[] {
  const result: (T | undefined)[] = new Array(options.length);
  const floaters: T[] = [];
  options.forEach((o, i) => {
    if (o.anchored) result[i] = o;
    else floaters.push(o);
  });
  for (let i = floaters.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [floaters[i], floaters[j]] = [floaters[j]!, floaters[i]!];
  }
  let fi = 0;
  for (let i = 0; i < result.length; i++) {
    if (result[i] === undefined) result[i] = floaters[fi++];
  }
  return result as T[];
}

/**
 * Reorders options to a previously-captured id order (from `shuffleWithAnchors`), tolerating
 * options added or removed since — anything no longer present is dropped, anything new is appended
 * at the end rather than lost. `null` order means "authored order", a no-op.
 */
export function reorderByIds<T extends OrderableOption>(options: T[], order: string[] | null): T[] {
  if (!order) return options;
  const byId = new Map(options.map(o => [o.id, o]));
  const ordered = order.map(id => byId.get(id)).filter((o): o is T => !!o);
  const seen = new Set(order);
  return [...ordered, ...options.filter(o => !seen.has(o.id))];
}
